// Единая строка показателя (D-3, §2.3).
// Source: docs/specs/design-revision/d-design-system.md §2.3 + §4.
//
// Одна и та же строка была написана трижды — в AttentionList, ChangesList и
// BiomarkerCard, — и три раза по-разному: рамка `border-gray-200` против
// `border-border`, кегли 14/13, отступы p-3 против p-3.5, значение 15px
// против 18px. Следствие видно на HEAL-75: «добавить метку в строку» превратилось в
// правку двух файлов, которые обязаны совпасть, и ни один тест не поймал бы,
// если бы они разошлись.
//
// `variant` управляет тем, ЧТО показано, а не тем, КАК выглядит. Рамка,
// отступы, полоска статуса, кегли и поведение при длинном имени — общие.

import type { ReactNode } from 'react';
import { ChevronRight } from 'lucide-react';
import { capitalizeFirst, cn, formatDeltaValue, formatNumber } from '@/lib/utils';
import { displayTier, STRIPE_BY_TIER, VALUE_TEXT_BY_TIER } from '@/lib/biomarkerStatus';
import { HistoryHint } from '@/components/dashboard/HistoryHint';
import type { ClinicalStatus, DashboardDelta, DeltaColor, OptimumStatus } from '@/api/types';

/**
 * `attention` — блок «Вне нормы» на Главной: направление словом, шеврон.
 * `catalog`   — каталог, орган, экран ревью: норма и единица.
 * `change`    — блок «Что изменилось»: было → стало и пилюля дельты.
 */
export type MarkerRowVariant = 'attention' | 'catalog' | 'change';

export type MarkerRowProps = {
  name: string;
  variant: MarkerRowVariant;
  value: number;
  unit: string;
  /**
   * HEAL-33: качественный ответ («не обнаружено»). Если задан — показываем
   * его вместо числа: в `value` у таких показателей порядковая оценка (0/1),
   * нужная только для сравнения с нормой.
   */
  valueText?: string | null;
  normalRangeLabel?: string;
  clinicalStatus?: ClinicalStatus;
  optimumStatus?: OptimumStatus;
  delta?: DashboardDelta | null;
  hasHistory?: boolean;
  /** Сторона отклонения словом. Стрелку здесь НЕ рисуем — ↑↓ значит динамику. */
  direction?: 'above' | 'below' | null;
  /** Только для `change`: предыдущее значение, строка «было → стало». */
  prevValue?: number;
  onClick?: () => void;
};

const DELTA_PILL_BY_COLOR: Record<DeltaColor, string> = {
  good: 'bg-norm/15 text-norm',
  bad: 'bg-bad/15 text-bad-text',
  neutral: 'bg-muted text-muted-foreground',
};

function directionWord(direction: 'above' | 'below' | null | undefined): string {
  if (direction === 'below') return 'ниже нормы';
  if (direction === 'above') return 'выше нормы';
  return 'вне нормы';
}

function deltaText(delta: DashboardDelta): string {
  return `${delta.delta_value >= 0 ? '↑ +' : '↓ '}${formatDeltaValue(delta.delta_value)}`;
}

export function MarkerRow({
  name,
  variant,
  value,
  unit,
  valueText = null,
  normalRangeLabel,
  clinicalStatus,
  optimumStatus = null,
  delta = null,
  hasHistory = false,
  direction = null,
  prevValue,
  onClick,
}: MarkerRowProps) {
  const Tag = onClick ? 'button' : 'div';
  // У изменения статуса в ответе нет (HeadlineChange его не отдаёт), поэтому
  // полоски там не будет: выдуманная полоска хуже отсутствующей.
  const tier = clinicalStatus ? displayTier(clinicalStatus, optimumStatus) : null;

  let subtitle: ReactNode = null;
  if (variant === 'change' && prevValue !== undefined) {
    subtitle = `${formatNumber(prevValue)} → ${formatNumber(value)} ${unit}`;
  } else if (normalRangeLabel) {
    subtitle = `Норма: ${normalRangeLabel}`;
  }

  return (
    <Tag
      type={onClick ? 'button' : undefined}
      onClick={onClick}
      className={cn(
        // shrink-0: строки лежат в flex-col контейнере фиксированной высоты
        // (MobileFrame). Без него длинный список сжимается по высоте, а
        // overflow-hidden срезает низ — это и была «скомканность».
        'relative flex w-full shrink-0 items-start gap-3 overflow-hidden rounded-surface',
        'border border-border bg-surface p-3.5 text-left',
        onClick && 'hover-elevate active-elevate-2',
      )}
    >
      {tier && (
        <span
          aria-hidden
          className={cn('absolute left-0 top-0 h-full w-1', STRIPE_BY_TIER[tier])}
        />
      )}

      <div className={cn('min-w-0 flex-1', tier && 'pl-1')}>
        <div className="flex items-start gap-2">
          <span className="text-hl-body font-semibold text-foreground">{capitalizeFirst(name)}</span>
          {delta && variant !== 'change' && (
            <span
              className={cn(
                'rounded-full px-1.5 py-0.5 text-hl-small font-semibold leading-none',
                DELTA_PILL_BY_COLOR[delta.delta_color],
              )}
            >
              {deltaText(delta)}
            </span>
          )}
        </div>
        {subtitle && <div className="text-hl-small text-muted-foreground">{subtitle}</div>}
        {hasHistory && <HistoryHint qualitative={Boolean(valueText)} />}
      </div>

      {variant === 'change' && delta ? (
        <span
          className={cn(
            'flex-shrink-0 rounded-full px-2 py-0.5 text-hl-small font-semibold',
            DELTA_PILL_BY_COLOR[delta.delta_color],
          )}
        >
          {deltaText(delta)}
        </span>
      ) : (
        <div className="text-right">
          <div
            className={cn(
              'font-bold leading-tight',
              // Слово длиннее числа — иначе «не обнаружено» ломает строку.
              valueText ? 'text-hl-secondary' : 'text-lg',
              tier ? VALUE_TEXT_BY_TIER[tier] : 'text-foreground',
            )}
          >
            {valueText ? capitalizeFirst(valueText) : formatNumber(value)}
          </div>
          {!valueText && <div className="text-hl-small text-muted-foreground">{unit}</div>}
          {variant === 'attention' && (
            <div className="text-hl-small font-semibold text-bad-text">
              {/* D-30: остаётся красной. Это единственное место, где названо
                  направление отклонения, и приглушать его нечестно —
                  контраст чинится текстовым тиром цвета, а не нейтралью. */}
              {directionWord(direction)}
            </div>
          )}
        </div>
      )}

      {variant === 'attention' && onClick && (
        <ChevronRight className="mt-1 h-4 w-4 flex-shrink-0 text-muted-foreground" aria-hidden="true" />
      )}
    </Tag>
  );
}
